"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "@/i18n/I18nProvider";

interface Step7DoubtsDialogProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  onContinue: () => void;
}

const MAX_LENGTH = 300;

export function Step7DoubtsDialog({ value, onChange, onClose, onContinue }: Step7DoubtsDialogProps) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const [showError, setShowError] = useState(false);
  const closeRef = useRef(onClose);

  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the dialog, leaving keyboard activation to a direct tap on the field.
    dialogRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
      }
      if (event.key !== "Tab") return;
      const controls = dialogRef.current?.querySelectorAll<HTMLElement>("textarea, button");
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (document.activeElement === dialogRef.current) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:items-center">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="step7-doubts-dialog-title"
        aria-describedby="step7-doubts-dialog-description"
        tabIndex={-1}
        className="max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-2xl border border-[#800000] bg-[#fdf2f2] p-5 text-slate-800 shadow-2xl outline-none sm:p-6"
      >
        <h3 id="step7-doubts-dialog-title" className="font-title text-xl font-bold text-[#800000]">
          {t("step7.doubts_popup.title")}
        </h3>
        <p id="step7-doubts-dialog-description" className="mt-2 text-sm">
          {t("step7.doubts_popup.description")}
        </p>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            // Portals still bubble React events to the wizard's form.
            event.stopPropagation();
            if (!value.trim()) {
              setShowError(true);
              fieldRef.current?.focus();
              return;
            }
            onContinue();
          }}
        >
          <label htmlFor="step7-popup-doubts" className="form-label-compact mt-4">
            {t("step7.doubts.label")}
          </label>
          <textarea
            ref={fieldRef}
            id="step7-popup-doubts"
            name="step7_popup_doubts"
            rows={4}
            maxLength={MAX_LENGTH}
            value={value}
            onChange={(event) => {
              onChange(event.target.value.slice(0, MAX_LENGTH));
              setShowError(false);
            }}
            autoComplete="off"
            placeholder={t("step7.doubts.placeholder")}
            aria-invalid={showError}
            aria-describedby={showError ? "step7-popup-doubts-error" : undefined}
            className="form-input-compact w-full resize-none focus:border-[#800000] focus:outline-none"
            style={{ fontSize: "16px" }}
          />
          <p className="mt-1 text-right text-xs text-gray-500">{value.length}/{MAX_LENGTH}</p>
          {showError && <p id="step7-popup-doubts-error" role="alert" className="mt-1 text-sm text-red-600">{t("step7.doubts.error")}</p>}
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <button type="submit" className="rounded-lg bg-emerald-700 px-4 py-2 font-bold text-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
              {t("step7.doubts_popup.continue")}
            </button>
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-400 bg-white px-4 py-2 font-bold focus:outline-none focus:ring-2 focus:ring-slate-500">
              {t("common.back")}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
